import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  loadShow,
  runModule as runModuleThunk,
  runAllModules,
  checkCacheStatus,
  refreshCache,
  exportPDF,
  setCurrentTrack,
  setCurrentModule,
  clearShowData,
  trackEvent,
} from '../../redux/slices/analyze13Slice';

import T from './theme';
import { MODULES } from './config.jsx';
import {
  LoadingSkeleton,
  KpiChips,
} from './components/Shared.jsx';
import { Sidebar } from './components/Sidebar.jsx';
import { MobileModulePicker } from './components/MobileModulePicker.jsx';
import { renderModuleOutput } from './modules/index.jsx';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Analyze13() {
  const dispatch = useDispatch();
  const {
    currentRssUrl,
    showData,
    loadingShow,
    currentTrack,
    currentModule,
    analysisResults,
    loadingModules,
    moduleErrors,
    moduleTimestamps,
    runningAll,
    cacheStatus,
    exportingPDF,
    error,
  } = useSelector((state) => state.analyze13);

  const { currentPodcast } = useSelector((state) => state.podcasts);

  // Derived
  const trackModules = MODULES[currentTrack];
  const activeModuleConfig = trackModules.find((m) => m.id === currentModule) || trackModules[0];
  const activeResult = analysisResults[currentModule];
  const activeLoading = !!loadingModules[currentModule];
  const activeError = moduleErrors[currentModule];
  const activeTimestamp = moduleTimestamps[currentModule] || null;
  const activeIsStale = activeTimestamp ? (Date.now() - activeTimestamp) > 14 * 24 * 60 * 60 * 1000 : false;

  // Freshness bar info
  const freshnessText = useCallback(() => {
    if (!cacheStatus?.lastAnalyzed) return null;
    const lastDate = new Date(cacheStatus.lastAnalyzed).toLocaleDateString();
    const daysOld = Math.floor((Date.now() - new Date(cacheStatus.lastAnalyzed)) / (1000 * 60 * 60 * 24));
    const nextRefresh = Math.max(0, 7 - daysOld);
    return { lastDate, daysOld, nextRefresh };
  }, [cacheStatus])();

  // Auto-load when currentPodcast changes
  useEffect(() => {
    const feedUrl = currentPodcast?.feedUrl || currentPodcast?.appleUrl || currentPodcast?.collectionViewUrl;
    if (!feedUrl) {
      dispatch(clearShowData());
      return;
    }
    const run = async () => {
      const result = await dispatch(loadShow(feedUrl));
      if (!result.error) {
        dispatch(checkCacheStatus(feedUrl));
        const firstId = MODULES[currentTrack][0].id;
        dispatch(setCurrentModule(firstId));
      }
    };
    run();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPodcast]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSelectModule = (moduleId) => {
    dispatch(setCurrentModule(moduleId));
  };

  // Episode index for Episode Optimizer dropdown
  const [episodeIndex, setEpisodeIndex] = useState(0);
  const isEpisodeOptimizer = currentModule === 'episode_optimizer_v2';
  const episodes = showData?.episodes || [];

  const handleRunModule = () => {
    if (!currentRssUrl) return;
    const args = { moduleId: currentModule, rssUrl: currentRssUrl };
    if (isEpisodeOptimizer && episodeIndex > 0) args.episodeIndex = episodeIndex;
    dispatch(runModuleThunk(args));
    dispatch(trackEvent({
      event: 'module_run',
      rssUrl: currentRssUrl,
      showTitle: showData?.show?.title || '',
      podcastId: showData?.show?.podcastId || null,
      moduleId: currentModule,
      track: currentTrack,
    }));
  };

  const handleRunAll = () => {
    if (!currentRssUrl) return;
    const moduleIds = MODULES[currentTrack].map((m) => m.id);
    dispatch(runAllModules({ moduleIds, rssUrl: currentRssUrl }));
  };

  const handleSwitchTrack = (track) => {
    dispatch(setCurrentTrack(track));
    const firstId = MODULES[track][0].id;
    dispatch(setCurrentModule(firstId));
  };

  const handleExportPDF = () => {
    if (currentRssUrl) dispatch(exportPDF({ rssUrl: currentRssUrl, track: currentTrack }));
  };

  const handleRefreshCache = () => {
    if (currentRssUrl) dispatch(refreshCache(currentRssUrl));
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', background: T.bg, color: T.text, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', minHeight: 0 }}>

      {/* ── Top Bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, px: { xs: 1.5, md: 2 }, py: { xs: 0 }, background: 'transparent', borderBottom: `1px solid ${T.border}`, minHeight: 56, height: 56, flexShrink: 0, zIndex: 10 }}>

        {/* Track tabs */}
        <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center', height: '100%', flex: 1 }}>
          {['owner', 'research'].map((track) => {
            const label = track === 'owner' ? 'My Podcast' : 'Research';
            const isActive = currentTrack === track;
            return (
              <Box
                key={track}
                component="button"
                onClick={() => handleSwitchTrack(track)}
                disabled={!showData}
                sx={{
                  height: { md: '100%' },
                  px: { xs: 1.25, md: 0 },
                  py: { xs: 0.5, md: 0 },
                  background: { xs: isActive ? 'rgba(249,115,22,0.15)' : 'none', md: 'none' },
                  border: 'none',
                  borderBottom: { md: `2px solid ${isActive ? T.primary : 'transparent'}` },
                  borderRadius: { xs: 1, md: 0 },
                  color: isActive ? T.primary : T.textSecondary,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: showData ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  position: { md: 'relative' },
                  top: { md: 1 },
                  '&:hover': showData ? { color: isActive ? T.primary : T.text } : {},
                }}
              >
                {label}
              </Box>
            );
          })}
          {/* KPI chips — visible on desktop when a show is loaded */}
          <KpiChips showData={showData} analysisResults={analysisResults} />
        </Box>

      </Box>

      {/* ── Error banner ── */}
      {error && (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.25, background: 'rgba(239,68,68,0.1)', borderBottom: `1px solid rgba(239,68,68,0.3)`, fontSize: 13, color: T.error }}>
          {error}
        </Box>
      )}

      {/* ── Freshness bar ── */}
      {freshnessText && (
        <Box sx={{
          px: { xs: 2, md: 3 }, py: 1.25,
          background: freshnessText.daysOld > 7 ? 'rgba(234,179,8,0.1)' : T.card,
          borderBottom: `1px solid ${T.border}`,
          fontSize: 13,
          color: freshnessText.daysOld > 7 ? T.warning : T.textSecondary,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          flexShrink: 0,
        }}>
          As of {freshnessText.lastDate} · Data cached — next refresh available in {freshnessText.nextRefresh} day{freshnessText.nextRefresh !== 1 ? 's' : ''}
          {freshnessText.daysOld > 7 && (
            <Box
              component="button"
              onClick={handleRefreshCache}
              sx={{ ml: 1, background: 'none', border: 'none', color: T.primary, cursor: 'pointer', fontSize: 13, p: 0, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              refresh now
            </Box>
          )}
        </Box>
      )}

      {/* ── Mobile module picker ── */}
      {showData && (
        <MobileModulePicker
          track={currentTrack}
          currentModule={currentModule}
          onSelectModule={handleSelectModule}
          moduleLoading={loadingModules}
        />
      )}

      {/* ── Main layout ── */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* Sidebar — shown only when a show is loaded (hidden on mobile, see MobileModulePicker above) */}
        {showData && (
          <Sidebar
            track={currentTrack}
            currentModule={currentModule}
            onSelectModule={handleSelectModule}
            onRunAll={handleRunAll}
            moduleLoading={loadingModules}
            onExportPDF={handleExportPDF}
            exportingPDF={exportingPDF}
            showData={showData}
            runningAll={runningAll}
            analysisResults={analysisResults}
            moduleErrors={moduleErrors}
          />
        )}

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: { xs: 2, md: 3 }, minWidth: 0 }}>

          {/* Empty state — no podcast selected */}
          {!showData && !loadingShow && (
            <Box sx={{ textAlign: 'center', py: { xs: 5, md: 8 } }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1, color: T.text }}>Select a podcast to begin</Typography>
              <Typography sx={{ fontSize: 14, color: T.textSecondary }}>
                Search for a podcast using the search bar above.
              </Typography>
            </Box>
          )}

          {/* Module view */}
          {showData && activeModuleConfig && (
            <>
              {/* Module header */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 700, mb: 1, color: T.text }}>{activeModuleConfig.name}</Typography>
                <Typography sx={{ fontSize: 14, color: T.textSecondary, mb: activeTimestamp ? 1.5 : 0 }}>{activeModuleConfig.description}</Typography>

                {/* Run-date strip */}
                {activeTimestamp && !activeLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 12, color: T.textSecondary }}>
                      Analysis run on{' '}
                      <Box component="span" sx={{ color: T.text, fontWeight: 500 }}>
                        {new Date(activeTimestamp).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </Box>
                    </Typography>
                    {activeIsStale && (
                      <Box
                        component="button"
                        onClick={handleRunModule}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5,
                          background: 'none', border: `1px solid ${T.border}`, borderRadius: 4,
                          color: T.textSecondary, fontSize: 12, cursor: 'pointer', px: 1.25, py: 0.4,
                          transition: 'all 0.15s',
                          '&:hover': { borderColor: T.primary, color: T.primary },
                        }}
                      >
                        <RefreshIcon sx={{ fontSize: 13 }} />
                        Re-run
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Episode selector (Episode Optimizer only) */}
              {isEpisodeOptimizer && episodes.length > 1 && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, whiteSpace: 'nowrap' }}>Episode:</Typography>
                  <Box
                    component="select"
                    value={episodeIndex}
                    onChange={(e) => setEpisodeIndex(Number(e.target.value))}
                    sx={{
                      flex: 1, maxWidth: 500, px: 1.5, py: 1,
                      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1,
                      color: T.text, fontSize: 13, cursor: 'pointer',
                      '&:focus': { outline: 'none', borderColor: T.primary },
                    }}
                  >
                    {episodes.slice(0, 50).map((ep, idx) => (
                      <option key={idx} value={idx}>
                        {ep.title || `Episode ${idx + 1}`}
                      </option>
                    ))}
                  </Box>
                  <Box
                    component="button"
                    onClick={handleRunModule}
                    disabled={activeLoading || !currentRssUrl}
                    sx={{
                      px: 2, py: 0.85,
                      background: T.primary, border: 'none', borderRadius: 1, color: '#fff',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                      transition: 'opacity 0.15s',
                      '&:hover': { opacity: 0.88 },
                      '&:disabled': { opacity: 0.4, cursor: 'default' },
                    }}
                  >
                    {activeLoading ? 'Optimizing…' : 'Optimize This Episode'}
                  </Box>
                </Box>
              )}

              {/* Module panel */}
              <Box sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 1.5, p: { xs: 2, md: 3 }, maxWidth: '100%', overflowX: 'hidden', wordWrap: 'break-word', overflowWrap: 'break-word' }}>

                {/* Loading skeleton */}
                {activeLoading && <LoadingSkeleton />}

                {/* Error */}
                {!activeLoading && activeError && (
                  <Box sx={{ p: 2, background: 'rgba(239,68,68,0.1)', border: `1px solid rgba(239,68,68,0.3)`, borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 20, mb: 1 }}>❌</Typography>
                    <Typography sx={{ fontWeight: 600, mb: 0.5, color: T.text }}>Analysis Failed</Typography>
                    <Typography sx={{ fontSize: 13, color: T.textSecondary }}>{activeError}</Typography>
                  </Box>
                )}

                {/* Ready state (no result, not loading) — just the run button, no duplicate name/desc */}
                {!activeLoading && !activeError && !activeResult && (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Box
                      component="button"
                      onClick={handleRunModule}
                      disabled={!currentRssUrl}
                      sx={{
                        px: 3, py: 1.25,
                        background: T.primary,
                        border: 'none',
                        borderRadius: 1.5,
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'opacity 0.15s',
                        '&:hover': { opacity: 0.88 },
                        '&:disabled': { opacity: 0.4, cursor: 'default' },
                      }}
                    >
                      Run Analysis
                    </Box>
                  </Box>
                )}

                {/* Result — citations are rendered inline by each module's WhyThisMatters */}
                {!activeLoading && !activeError && activeResult && (
                  renderModuleOutput(currentModule, activeResult)
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Analyze13;
